import React, { useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../services/api';
import { useHistory } from 'react-router-dom';
import Icon from '../../components/Icon';
import { showToast } from '../../components/Toast';

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
};

const RegisterPage: React.FC = () => {
  const [displayName, setDisplayName] = useState('');
  const [familyMode, setFamilyMode] = useState<'create' | 'join'>('create');
  const [familyName, setFamilyName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { register, setFamily, refreshFamily } = useAuthStore();
  const history = useHistory();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const heroImage = isDesktop
    ? `${process.env.PUBLIC_URL}/login-bg.webp`
    : `${process.env.PUBLIC_URL}/login-bg-mobile.webp`;

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName || !email || !password) {
      setError('Please fill in all fields');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Za-z]/.test(password) || !/[0-9\W_]/.test(password)) {
      setError('Password needs at least one letter and one number or symbol');
      return;
    }
    if (password.toLowerCase() === email.trim().toLowerCase()) {
      setError('Password must not match your email address');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await register(email.trim(), password, displayName.trim());
      showToast('Account created!', 'success');

      if (familyMode === 'join' && inviteCode.trim()) {
        // Join existing family with invite code
        try {
          const res = await api.post('/family/join', { inviteCode: inviteCode.trim() });
          setFamily(res.data.family);
          await refreshFamily();
          history.replace('/app/home');
        } catch (err: any) {
          setError(err.response?.data?.error || 'Invalid invite code. You can join a family later from Settings.');
          history.replace('/family-setup');
        }
      } else if (familyMode === 'create' && familyName.trim()) {
        // Create new family
        try {
          const res = await api.post('/family/create', { name: familyName.trim() });
          setFamily(res.data.family);
          await refreshFamily();
          history.replace('/app/home');
        } catch {
          history.replace('/family-setup');
        }
      } else {
        history.replace('/family-setup');
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Registration failed. Please try again.';
      setError(msg);
      showToast('Registration failed', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen scrollY>
        <div className="fh-landing">
          <div
            className="fh-landing__photo"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
          <div className="fh-landing__panel">
            <div className="fh-auth__inner">
              {/* Back */}
              <button onClick={() => history.push('/')} className="fh-auth__back">
                <Icon name="chevron_left" size={20} color="#c5512d" />
                Back
              </button>

              {/* Header */}
              <div className="fh-auth__header">
                <img src="/icon-nobg.png" alt="FamHearth" className="fh-auth__icon" />
                <h1 className="fh-auth__title" style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontStyle: 'italic', fontSize: 44, lineHeight: 1 }}>
                  Welcome <span style={{ color: '#c5512d' }}>home.</span>
                </h1>
                <p className="fh-auth__subtitle">One quiet place for the people you live with. Let's set it up together.</p>
              </div>

              {/* Form */}
              <form onSubmit={handleRegister} aria-label="Registration form" className="fh-auth__form">
                <div className="fh-auth__field">
                  <label className="fh-auth__label">* Your name</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    autoComplete="name"
                    placeholder="e.g. Sarah Harrison"
                    className="fh-auth__input"
                  />
                </div>
                <div className="fh-auth__field">
                  <label className="fh-auth__label">Family</label>
                  <div style={{ display: 'flex', gap: 4, padding: 3, background: 'rgba(15,25,35,0.7)', borderRadius: 10, marginBottom: 8 }}>
                    <button
                      type="button"
                      onClick={() => setFamilyMode('create')}
                      style={{
                        flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700,
                        fontFamily: 'Manrope, sans-serif', cursor: 'pointer',
                        background: familyMode === 'create' ? '#a43917' : 'transparent',
                        color: familyMode === 'create' ? '#fff' : 'rgba(255,255,255,0.5)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      Create new
                    </button>
                    <button
                      type="button"
                      onClick={() => setFamilyMode('join')}
                      style={{
                        flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700,
                        fontFamily: 'Manrope, sans-serif', cursor: 'pointer',
                        background: familyMode === 'join' ? '#a43917' : 'transparent',
                        color: familyMode === 'join' ? '#fff' : 'rgba(255,255,255,0.5)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      Join with code
                    </button>
                  </div>
                  {familyMode === 'create' ? (
                    <input
                      type="text"
                      value={familyName}
                      onChange={(e) => setFamilyName(e.target.value)}
                      placeholder="e.g. The Harrisons"
                      className="fh-auth__input"
                    />
                  ) : (
                    <input
                      type="text"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                      placeholder="Enter invite code"
                      className="fh-auth__input"
                      style={{ letterSpacing: '0.15em', textTransform: 'uppercase' }}
                    />
                  )}
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 6, lineHeight: 1.4 }}>
                    {familyMode === 'create'
                      ? 'Each family member signs up with their own email and joins using an invite code you can share.'
                      : 'Ask your family admin for the invite code — find it in More > Invite Code.'}
                  </p>
                </div>
                <div className="fh-auth__field">
                  <label className="fh-auth__label">* Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@example.com"
                    className="fh-auth__input"
                  />
                </div>
                <div className="fh-auth__field">
                  <label className="fh-auth__label">* Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                    className="fh-auth__input"
                  />
                  <p className="fh-field-error" style={{color: 'rgba(255,255,255,0.4)'}}>At least 8 characters, with a letter and a number or symbol.</p>
                </div>
                {error && <p className="fh-auth__error">{error}</p>}
                <button type="submit" disabled={isLoading} className="fh-auth__submit">
                  {isLoading ? 'Creating account...' : 'Get Started'}
                </button>
              </form>

              {/* Footer */}
              <p className="fh-auth__footer">
                Already have an account?{' '}
                <button onClick={() => history.push('/login')} className="fh-auth__link">
                  Sign in
                </button>
              </p>

              <p className="fh-auth__terms">
                By creating an account you agree to our{' '}
                <a href="/terms" target="_blank" rel="noreferrer" className="fh-auth__link">terms of service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noreferrer" className="fh-auth__link">privacy policy</a>.
              </p>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default RegisterPage;
